/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { TOOLBAR_ITEM_TYPE } from '@veeam/components';

import type { ToolbarItems, ToolbarGroupItem, ButtonTypeItem, SeparatorTypeItem, GroupTypeItem } from '@veeam/components/lib/Toolbar/types';
import type { CommandMenuItemConfig } from '@veeam/components';

const isLoneSeparators = (items: CommandMenuItemConfig[]) => items.every(item => item.separator);

const processSingleItem = (item: ButtonTypeItem | SeparatorTypeItem | GroupTypeItem, index: number) => {
    if (item.type === TOOLBAR_ITEM_TYPE.separator) {
        return {
            separator: true,
            id: item.id ? String(item.id) : `separator-${index}`,
        };
    }

    if (item.type === TOOLBAR_ITEM_TYPE.group) {
        const commandMenuItem: CommandMenuItemConfig = {
            ...item,
            icon: item.iconSrc,
            id: item.id && String(item.id),
            items: item.items.map(processSingleItem),
            tooltip: item.text,
        };

        return commandMenuItem;
    }

    const commandMenuItem: CommandMenuItemConfig = {
        ...item,
        icon: item.iconSrc,
        id: item.id && String(item.id),
        items: [],
        tooltip: item.text,
    };

    return commandMenuItem;
};

export const toolbarActionToCommandAction = (
    toolbarActions: ToolbarItems = [],
    toolbarGroups: ToolbarGroupItem[] = []
): CommandMenuItemConfig[] => {
    const groupsIndex: Record<string, number> = {};
    const items: Record<number, CommandMenuItemConfig> = {};
    const groups: Record<string, CommandMenuItemConfig> = {};

    toolbarGroups.forEach((group) => {
        const id = String(group.id);

        groups[id] = {
            ...group,
            id,
            icon: group.iconSrc,
            items: [],
            tooltip: group.title,
            text: group.title,
        };
    });

    toolbarActions.forEach((item, index) => {
        if (!item || item.hidden) {
            return;
        }

        if (
            item.type !== TOOLBAR_ITEM_TYPE.button &&
            item.type !== TOOLBAR_ITEM_TYPE.separator &&
            item.type !== TOOLBAR_ITEM_TYPE.group
        ) {
            return;
        }

        if (item.type === TOOLBAR_ITEM_TYPE.group) {
            const commandMenuItem: CommandMenuItemConfig = {
                ...item,
                icon: item.iconSrc,
                id: String(item.id),
                tooltip: item.text,
                items: (item.items ?? [])
                    .filter(x => x)
                    .map(processSingleItem),
            };

            groupsIndex[item.id] = index;
            groups[item.id] = commandMenuItem;

            return;
        }

        const commandMenuItem = processSingleItem(item, index);

        if (item.groupId) {
            groupsIndex[item.groupId] = groupsIndex[item.groupId] ?? index;
            groups[item.groupId].items.push(commandMenuItem);

            return;
        }

        items[index] = commandMenuItem;
    });

    Object.values(groups).forEach((group) => {
        if (!group.items.length) {
            return;
        }

        if (isLoneSeparators(group.items)) {
            return;
        }

        items[groupsIndex[group.id]] = group;
    });

    const normalizedItems = Object.entries(items)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([index, value]) => value);

    if (isLoneSeparators(normalizedItems)) {
        return [];
    }

    return normalizedItems;
};
